/**
 * Card atom: container with border, padding, rounded corners.
 * Appearance from Card.styles. Interaction logic in Card.utils.
 * Supports onClick, keyboard (Enter/Space when interactive), disabled (aria + no click).
 *
 * @param variant - outlined | elevated | subtle | custom (no default colors; use className)
 * @param padding - none | sm | md | lg
 * @param onClick - optional
 * @param interactive - when true (default if onClick set), role=button, tabIndex=0, keyboard; set false for e.g. stopPropagation only
 * @param disabled - aria-disabled, onClick not fired
 */

import React from 'react';
import type { CardProps } from './Card.types';
import { getCardClasses } from './Card.styles';
import { createCardKeyDownHandler, getCardInteractionState } from './Card.utils';

export type { CardVariant, CardPadding, CardRounded } from './Card.types';
export type { CardProps } from './Card.types';

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      children,
      variant = 'subtle',
      padding = 'md',
      rounded = 'xl',
      className = '',
      onClick,
      interactive = undefined,
      disabled = false,
      onKeyDown,
      ...rest
    },
    ref
  ) => {
    const state = getCardInteractionState(onClick, interactive, disabled);
    const handleKeyDown = createCardKeyDownHandler(
      onClick,
      state.isClickable,
      state.isInteractive,
      onKeyDown
    );

    return (
      <div
        ref={ref}
        role={state.role}
        tabIndex={state.tabIndex}
        aria-disabled={state.ariaDisabled}
        className={getCardClasses(variant, padding, rounded, className)}
        onClick={disabled ? undefined : onClick}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';
